import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Collapse,
  Divider,
  Stack,
  Paper,
  Avatar,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add,
  MoreVert,
  Flag,
  Delete,
  ThumbUp,
  ThumbUpOutlined,
  Share,
  Bookmark,
  BookmarkBorder,
  Verified,
  Star,
  TrendingUp,
  FilterList,
  Sort,
  Close,
  Edit,
  Visibility,
  Comment,
  EmojiEvents,
  LocalFireDepartment
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { showAuthAlert } from '../../utils/authAlerts';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { listMovieReviews } from '../../graphql/queries';
import { createMovieReviewCustom, updateMovieReviewCustom } from '../../graphql/customMutations';
import PremiumRatingSystem from '../Common/PremiumRatingSystem';
import BadgedAvatar from '../Common/BadgedAvatar';
import hypeRatingService from '../../services/hypeRatingService';
import { interactionService } from '../../services/interactionService';

const SORT_OPTIONS = [
  { value: 'time', label: 'Latest', icon: TrendingUp },
  { value: 'likes', label: 'Most Liked', icon: ThumbUp },
  { value: 'rating', label: 'Highest Rated', icon: Star },
  { value: 'followers', label: 'Top Reviewers', icon: EmojiEvents }
];

const RATING_SYSTEMS = [
  { value: 'STARS', label: 'Stars', icon: '⭐' },
  { value: 'HEARTS', label: 'Hearts', icon: '❤️' },
  { value: 'THUMBS', label: 'Thumbs', icon: '👍' },
  { value: 'SENTIMENT', label: 'Sentiment', icon: '😊' },
  { value: 'TOMATO_POPCORN', label: 'Tomato & Popcorn', icon: '🍅🍿' }
];

const PremiumMovieReviews = ({ movieId, onReviewUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { addNotification } = useApp();
  const navigate = useNavigate();
  
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddReview, setShowAddReview] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 0, review: '', ratingSystem: 'STARS' });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState('time');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [likedReviews, setLikedReviews] = useState(new Set());
  const [bookmarkedReviews, setBookmarkedReviews] = useState(new Set());
  const [authors, setAuthors] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const [editingReview, setEditingReview] = useState(null);
  const [editData, setEditData] = useState({ rating: 0, review: '', ratingSystem: 'STARS' });
  const [deletingReview, setDeletingReview] = useState(null);
  const [userExistingReview, setUserExistingReview] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, [movieId, sortBy, page]);

  // Load user interaction states
  useEffect(() => {
    if (user?.id && reviews.length > 0) {
      loadUserInteractions();
    }
  }, [user?.id, reviews.length]);

  const loadUserInteractions = async () => {
    if (!user?.id) return;
    
    try {
      const likePromises = reviews.map(review => 
        interactionService.isLiked('review', review.id, user.id, 'MOVIE')
      );
      const bookmarkPromises = reviews.map(review => 
        interactionService.isBookmarkedUniversal('review', review.id, user.id)
      );
      
      const [likeResults, bookmarkResults] = await Promise.all([
        Promise.all(likePromises),
        Promise.all(bookmarkPromises)
      ]);
      
      const newLikedReviews = new Set();
      const newBookmarkedReviews = new Set();
      
      reviews.forEach((review, index) => {
        if (likeResults[index]) newLikedReviews.add(review.id);
        if (bookmarkResults[index]) newBookmarkedReviews.add(review.id);
      });
      
      setLikedReviews(newLikedReviews);
      setBookmarkedReviews(newBookmarkedReviews);
    } catch (error) {
      console.error('Error loading user interactions:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const client = generateClient();
      const filter = { movieID: { eq: movieId } };
      
      const result = await client.graphql({
        query: listMovieReviews,
        variables: {
          filter,
          limit: 10,
          nextToken: page > 1 ? `page-${page}` : null
        }
      });
      
      const reviewItems = result.data.listMovieReviews.items;
      
      // Fetch author details with badges (only for authenticated users)
      const authorPromises = user ? reviewItems.map(async (review) => {
        if (review.authorID && !authors[review.authorID]) {
          try {
            const authorResult = await client.graphql({
              query: `
                query GetUser($id: ID!) {
                  getUser(id: $id) {
                    id
                    username
                    displayName
                    avatar
                    verified
                    followersCount
                    activeBadges
                  }
                }
              `,
              variables: { id: review.authorID }
            });
            
            // Fetch user badges
            const badgesResult = await client.graphql({
              query: `
                query ListUserBadges($filter: ModelUserBadgeFilterInput) {
                  listUserBadges(filter: $filter) {
                    items {
                      badgeType
                      earnedAt
                    }
                  }
                }
              `,
              variables: {
                filter: { userID: { eq: review.authorID } }
              }
            });
            
            return { 
              [review.authorID]: {
                ...authorResult.data.getUser,
                badges: badgesResult.data.listUserBadges.items
              }
            };
          } catch (error) {
            console.error('Error fetching author:', error);
            return { [review.authorID]: null };
          }
        }
        return null;
      }) : [];
      
      const authorResults = await Promise.all(authorPromises);
      const newAuthors = { ...authors };
      authorResults.forEach(result => {
        if (result) Object.assign(newAuthors, result);
      });
      
      // Add fallback author info for reviews without author data (for unauthenticated users)
      reviewItems.forEach(review => {
        if (review.authorID && !newAuthors[review.authorID]) {
          newAuthors[review.authorID] = {
            id: review.authorID,
            username: 'User',
            displayName: 'Anonymous User',
            avatar: null,
            verified: false,
            followersCount: 0,
            activeBadges: [],
            badges: []
          };
        }
      });
      
      setAuthors(newAuthors);
      
      // Sort reviews
      let sortedReviews = [...reviewItems];
      switch (sortBy) {
        case 'likes':
          sortedReviews.sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0));
          break;
        case 'rating':
          sortedReviews.sort((a, b) => Math.abs(b.rating || 0) - Math.abs(a.rating || 0));
          break;
        case 'followers':
          sortedReviews.sort((a, b) => (newAuthors[b.authorID]?.followersCount || 0) - (newAuthors[a.authorID]?.followersCount || 0));
          break;
        case 'time':
        default:
          sortedReviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      }
      
      setReviews(sortedReviews);
      setTotalPages(Math.ceil((result.data.listMovieReviews.items.length || 0) / 10));
      
      // Check if current user already has a review
      if (user?.id) {
        const existingReview = sortedReviews.find(r => r.authorID === user.id);
        setUserExistingReview(existingReview || null);
      } else {
        setUserExistingReview(null);
      }
      
      // Update the parent component if callback provided
      if (onReviewUpdate) {
        onReviewUpdate();
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!user || !newReview.rating) return;
    
    // Prevent duplicate reviews - check if user already has a review
    if (userExistingReview) {
      setNotification({ 
        open: true, 
        message: 'You already have a review for this movie. Please edit your existing review instead.', 
        severity: 'warning' 
      });
      setShowAddReview(false);
      return;
    }
    
    setSubmitting(true);
    try {
      const client = generateClient();
      const reviewInput = {
        movieID: movieId,
        authorID: user.id,
        rating: newReview.rating,
        ratingSystem: newReview.ratingSystem
      };
      
      // Only add review text if provided
      if (newReview.review.trim()) {
        reviewInput.review = newReview.review.trim();
      }
      
      const result = await client.graphql({
        query: createMovieReviewCustom,
        variables: {
          input: reviewInput
        }
      });
      
      // Track interaction for hype rating (non-blocking, silent)
      try {
        hypeRatingService.trackInteraction('submit_review', movieId, user.id, { 
          rating: newReview.rating,
          ratingSystem: newReview.ratingSystem 
        }).catch(() => {
          // Silent fail - hype rating is non-critical
        });
      } catch {
        // Silent fail - hype rating is non-critical
      }
      
      setNewReview({ rating: 0, review: '', ratingSystem: 'STARS' });
      setShowAddReview(false);
      await fetchReviews();
      if (onReviewUpdate) onReviewUpdate();
      
      // Scroll to top of reviews section after publishing
      setTimeout(() => {
        const reviewsSection = document.querySelector('[data-reviews-section]');
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      setNotification({ 
        open: true, 
        message: newReview.review.trim() ? '🎉 Review published successfully!' : '⭐ Rating published successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error submitting review:', error);
      setNotification({ 
        open: true, 
        message: 'Failed to publish review', 
        severity: 'error' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeReview = async (reviewId) => {
    if (!user) {
      showAuthAlert(addNotification, navigate, 'LIKE');
      return;
    }
    
    try {
      const isLiked = likedReviews.has(reviewId);
      
      // Toggle like in database
      await interactionService.toggleLike('review', reviewId, 'MOVIE');
      
      // Track interaction for hype rating (non-blocking, silent)
      hypeRatingService.trackInteraction('like_review', movieId, user.id, { 
        reviewId, 
        action: isLiked ? 'unlike' : 'like' 
      }).catch(() => {
        // Silent fail - hype rating is non-critical
      });
      
      const newLikedReviews = new Set(likedReviews);
      if (isLiked) {
        newLikedReviews.delete(reviewId);
      } else {
        newLikedReviews.add(reviewId);
      }
      setLikedReviews(newLikedReviews);
      
      setReviews(prev => prev.map(review => 
        review.id === reviewId 
          ? { ...review, likesCount: (review.likesCount || 0) + (isLiked ? -1 : 1) }
          : review
      ));
      
      setNotification({ 
        open: true, 
        message: isLiked ? 'Like removed' : 'Review liked!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error liking review:', error);
      setNotification({ 
        open: true, 
        message: 'Failed to update like', 
        severity: 'error' 
      });
    }
  };

  const toggleExpandReview = (reviewId) => {
    const newExpanded = new Set(expandedReviews);
    if (newExpanded.has(reviewId)) {
      newExpanded.delete(reviewId);
    } else {
      newExpanded.add(reviewId);
    }
    setExpandedReviews(newExpanded);
  };

  const getReviewPreview = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleEditReview = async () => {
    if (!user || !editData.rating || !editingReview) return;
    
    setSubmitting(true);
    try {
      const client = generateClient();
      const updateInput = {
        id: editingReview.id,
        rating: editData.rating,
        ratingSystem: editData.ratingSystem
      };
      
      // Only update review text if provided
      if (editData.review.trim()) {
        updateInput.review = editData.review.trim();
      } else {
        updateInput.review = null; // Clear review text if empty
      }
      
      await client.graphql({
        query: updateMovieReviewCustom,
        variables: { input: updateInput }
      });
      
      setEditingReview(null);
      setEditData({ rating: 0, review: '', ratingSystem: 'STARS' });
      await fetchReviews();
      if (onReviewUpdate) onReviewUpdate();
      
      // Scroll to reviews section after updating
      setTimeout(() => {
        const reviewsSection = document.querySelector('[data-reviews-section]');
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      setNotification({ 
        open: true, 
        message: '✏️ Review updated successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error updating review:', error);
      setNotification({ 
        open: true, 
        message: 'Failed to update review', 
        severity: 'error' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const startEditReview = (review) => {
    setEditingReview(review);
    setEditData({
      rating: review.rating,
      review: review.review || '',
      ratingSystem: review.ratingSystem || 'STARS'
    });
    setMenuAnchor(null);
  };

  const isEdited = (review) => {
    return review.updatedAt && new Date(review.updatedAt) > new Date(review.createdAt);
  };

  const handleDeleteReview = async (reviewId) => {
    if (!user || !reviewId) return;
    
    setDeletingReview(reviewId);
    try {
      const client = generateClient();
      await client.graphql({
        query: `
          mutation DeleteMovieReview($input: DeleteMovieReviewInput!) {
            deleteMovieReview(input: $input) {
              id
            }
          }
        `,
        variables: { input: { id: reviewId } }
      });
      
      await fetchReviews();
      if (onReviewUpdate) onReviewUpdate();
      
      // Clear userExistingReview if user deleted their own review
      if (reviewId === userExistingReview?.id) {
        setUserExistingReview(null);
      }
      
      // Scroll to reviews section after deleting
      setTimeout(() => {
        const reviewsSection = document.querySelector('[data-reviews-section]');
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      setNotification({ 
        open: true, 
        message: '🗑️ Review deleted successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error deleting review:', error);
      setNotification({ 
        open: true, 
        message: 'Failed to delete review', 
        severity: 'error' 
      });
    } finally {
      setDeletingReview(null);
      setMenuAnchor(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: isMobile ? 2 : 3, 
          mb: isMobile ? 2 : 3, 
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,193,7,0.05) 100%)'
            : 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0.05) 100%)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700}>
              🎬 Reviews & Ratings
            </Typography>
            <Chip 
              label={reviews.length} 
              color="primary" 
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
          
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Tooltip title="Filter & Sort">
              <IconButton 
                onClick={() => setShowFilters(!showFilters)}
                color={showFilters ? "primary" : "default"}
                size={isMobile ? "small" : "medium"}
              >
                <FilterList />
              </IconButton>
            </Tooltip>
            {!showAddReview && !editingReview && (
              <Button
                startIcon={!isMobile ? (userExistingReview ? <Edit /> : <Add />) : null}
                onClick={() => {
                  if (!user) {
                    showAuthAlert(addNotification, navigate, 'REVIEW');
                    return;
                  }
                  if (userExistingReview) {
                    startEditReview(userExistingReview);
                  } else {
                    setShowAddReview(true);
                  }
                }}
                variant="contained"
                color={userExistingReview ? "warning" : "primary"}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: isMobile ? 'auto' : 'unset'
                }}
              >
                {isMobile ? (userExistingReview ? '✏️' : '✍️') : (userExistingReview ? 'Edit Your Review' : 'Write Review')}
              </Button>
            )}
          </Box>
        </Box>

        {/* Filters */}
        <Collapse in={showFilters}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={(e) => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map(option => {
                  const IconComponent = option.icon;
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconComponent fontSize="small" />
                        {option.label}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </Collapse>
      </Paper>

      {/* Auth Prompt */}
      {!user && (
        <Fade in={true}>
          <Card sx={{ 
            mb: 3, 
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(76,175,80,0.1) 0%, rgba(76,175,80,0.05) 100%)'
              : 'linear-gradient(135deg, rgba(33,150,243,0.1) 0%, rgba(33,150,243,0.05) 100%)',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 3
          }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                ✨ Join the Conversation
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Sign in to rate movies and share your thoughts with the community!
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate('/auth')}
                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}
              >
                Sign In to Rate & Review
              </Button>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Edit Review Form */}
      {editingReview && user && (
        <Zoom in={true}>
          <Card sx={{ 
            mb: isMobile ? 2 : 3, 
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255,152,0,0.1) 0%, rgba(255,152,0,0.05) 100%)'
              : 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)',
            border: '2px solid',
            borderColor: 'warning.main',
            borderRadius: 3
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={700} color="warning.main">
                  ✏️ Edit Your Review
                </Typography>
                <IconButton onClick={() => setEditingReview(null)} size="small">
                  <Close />
                </IconButton>
              </Box>
              
              {/* Rating System Selector */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Rating Style
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {RATING_SYSTEMS.map(system => (
                    <Chip
                      key={system.value}
                      label={isMobile ? system.icon : `${system.icon} ${system.label}`}
                      onClick={() => {
                        setEditData(prev => ({ 
                          ...prev, 
                          ratingSystem: system.value,
                          // Keep existing rating if it's valid for the new system, otherwise set default
                          rating: system.value === 'TOMATO_POPCORN' && (prev.rating < -5 || prev.rating > 5 || prev.rating === 0) ? 1 :
                                  system.value === 'THUMBS' && (prev.rating !== 1 && prev.rating !== -1) ? 1 :
                                  system.value !== 'TOMATO_POPCORN' && system.value !== 'THUMBS' && (prev.rating < 1 || prev.rating > 5) ? 1 :
                                  prev.rating
                        }));
                      }}
                      color={editData.ratingSystem === system.value ? "warning" : "default"}
                      variant={editData.ratingSystem === system.value ? "filled" : "outlined"}
                      size={isMobile ? "small" : "medium"}
                      sx={{ 
                        fontWeight: 600,
                        '&:hover': { transform: 'scale(1.05)' },
                        minWidth: isMobile ? 'auto' : 'unset'
                      }}
                    />
                  ))}
                </Box>
              </Box>
              
              {/* Rating Input */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Update your rating
                </Typography>
                <Paper sx={{ p: isMobile ? 2 : 3, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <PremiumRatingSystem
                    value={editData.rating}
                    onChange={(value) => setEditData(prev => ({ ...prev, rating: value }))}
                    system={editData.ratingSystem}
                    size="large"
                    animated={true}
                  />
                </Paper>
              </Box>
              
              {/* Review Text */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Update your thoughts (optional)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Update your thoughts about this movie..."
                  value={editData.review}
                  onChange={(e) => setEditData(prev => ({ ...prev, review: e.target.value }))}
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      borderRadius: 2
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {editData.review.length}/500 characters • Leave empty to remove review text
                </Typography>
              </Box>
              
              {/* Submit Buttons */}
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button 
                  onClick={() => setEditingReview(null)}
                  variant="outlined"
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleEditReview}
                  disabled={!editData.rating || submitting}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 140
                  }}
                >
                  {submitting ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} color="inherit" />
                      Updating...
                    </Box>
                  ) : (
                    `💾 Update ${editData.review.trim() ? 'Review' : 'Rating'}`
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Zoom>
      )}

      {/* Add Review Form */}
      {showAddReview && user && !editingReview && (
        <Zoom in={true}>
          <Card sx={{ 
            mb: isMobile ? 2 : 3, 
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(156,39,176,0.1) 0%, rgba(156,39,176,0.05) 100%)'
              : 'linear-gradient(135deg, rgba(103,58,183,0.1) 0%, rgba(103,58,183,0.05) 100%)',
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 3
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={700} color="primary">
                  ✍️ Write Your Review
                </Typography>
                <IconButton onClick={() => setShowAddReview(false)} size="small">
                  <Close />
                </IconButton>
              </Box>
              
              {/* Rating System Selector */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Choose Rating Style
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {RATING_SYSTEMS.map(system => (
                    <Chip
                      key={system.value}
                      label={isMobile ? system.icon : `${system.icon} ${system.label}`}
                      onClick={() => {
                        setNewReview(prev => ({ 
                          ...prev, 
                          ratingSystem: system.value, 
                          rating: system.value === 'TOMATO_POPCORN' ? 1 : system.value === 'THUMBS' ? 1 : 1
                        }));
                      }}
                      color={newReview.ratingSystem === system.value ? "primary" : "default"}
                      variant={newReview.ratingSystem === system.value ? "filled" : "outlined"}
                      size={isMobile ? "small" : "medium"}
                      sx={{ 
                        fontWeight: 600,
                        '&:hover': { transform: 'scale(1.05)' },
                        minWidth: isMobile ? 'auto' : 'unset'
                      }}
                    />
                  ))}
                </Box>
              </Box>
              
              {/* Rating Input */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Rate this movie
                </Typography>
                <Paper sx={{ p: isMobile ? 2 : 3, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <PremiumRatingSystem
                    value={newReview.rating}
                    onChange={(value) => setNewReview(prev => ({ ...prev, rating: value }))}
                    system={newReview.ratingSystem}
                    size="large"
                    animated={true}
                  />
                </Paper>
              </Box>
              
              {/* Review Text */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Share your thoughts (optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  💡 You can publish just a rating or add a written review - both are valuable!
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="What did you think about this movie? Share your experience, favorite scenes, or what made it special... (Optional - you can skip this and just publish your rating)"
                  value={newReview.review}
                  onChange={(e) => setNewReview(prev => ({ ...prev, review: e.target.value }))}
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      borderRadius: 2
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {newReview.review.length}/500 characters • Review text is optional
                </Typography>
              </Box>
              
              {/* Submit Buttons */}
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button 
                  onClick={() => setShowAddReview(false)}
                  variant="outlined"
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitReview}
                  disabled={!newReview.rating || submitting}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 140
                  }}
                >
                  {submitting ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} color="inherit" />
                      Publishing...
                    </Box>
                  ) : (
                    newReview.review.trim() ? '🚀 Publish Review' : '⭐ Publish Rating'
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Zoom>
      )}

      {/* Reviews List */}
      <div data-reviews-section>
      {reviews.length === 0 ? (
        <Paper sx={{ p: isMobile ? 3 : 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            🎭 No reviews yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Be the first to share your thoughts about this movie!
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={isMobile ? 1.5 : 2}>
          {reviews.map((review, index) => (
            <Zoom in={true} timeout={200 + index * 100} key={review.id}>
              <Card sx={{ 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                '&:hover': {
                  boxShadow: theme.shadows[8],
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Box display="flex" gap={2}>
                    {/* User Avatar */}
                    <BadgedAvatar
                      user={{
                        id: review.authorID,
                        username: authors[review.authorID]?.username,
                        displayName: authors[review.authorID]?.displayName || authors[review.authorID]?.username,
                        avatar: authors[review.authorID]?.avatar,
                        activeBadges: authors[review.authorID]?.activeBadges || [],
                        verified: authors[review.authorID]?.verified
                      }}
                      badges={authors[review.authorID]?.badges || []}
                      size={isMobile ? 48 : 56}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/profile/${authors[review.authorID]?.username}`)}
                    />
                    
                    <Box flex={1}>
                      {/* User Info & Actions */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography 
                              variant="subtitle1" 
                              fontWeight={700}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/profile/${authors[review.authorID]?.username}`)}
                            >
                              {authors[review.authorID]?.displayName || authors[review.authorID]?.username || 'Anonymous'}
                            </Typography>
                            {authors[review.authorID]?.verified && (
                              <Verified color="primary" sx={{ fontSize: 18 }} />
                            )}
                            {authors[review.authorID]?.followersCount > 1000 && (
                              <Chip 
                                label={`${Math.floor(authors[review.authorID].followersCount / 1000)}K followers`}
                                size="small"
                                color="secondary"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          
                          <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2} flexWrap="wrap">
                            <Box sx={{ minWidth: 'fit-content' }}>
                              <PremiumRatingSystem
                                value={review.rating}
                                system={review.ratingSystem || 'STARS'}
                                readOnly
                                size={isMobile ? "small" : "small"}
                                compact={isMobile}
                                showValue={true}
                                showLabel={false}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                              {new Date(review.createdAt).toLocaleDateString()}
                              {isEdited(review) && (
                                <Chip 
                                  label="edited" 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ 
                                    ml: 1, 
                                    height: 16, 
                                    fontSize: '0.6rem',
                                    color: 'text.secondary',
                                    borderColor: 'text.secondary'
                                  }} 
                                />
                              )}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {user && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setMenuAnchor(e.currentTarget);
                              setSelectedReview(review);
                            }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      
                      {/* Review Content */}
                      {review.review ? (
                        <Box mb={2}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              lineHeight: 1.7,
                              color: 'text.primary'
                            }}
                          >
                            {expandedReviews.has(review.id) 
                              ? review.review 
                              : getReviewPreview(review.review)
                            }
                          </Typography>
                          {review.review.length > 150 && (
                            <Button
                              size="small"
                              onClick={() => toggleExpandReview(review.id)}
                              sx={{ mt: 1, textTransform: 'none' }}
                            >
                              {expandedReviews.has(review.id) ? 'Show less' : 'Read more'}
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Box mb={2}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontStyle: 'italic', 
                              color: 'text.secondary',
                              bgcolor: 'action.hover',
                              p: 1.5,
                              borderRadius: 1,
                              border: '1px dashed',
                              borderColor: 'divider'
                            }}
                          >
                            ⭐ Rating only - no written review
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Actions */}
                      <Box 
                        display="flex" 
                        alignItems="center" 
                        gap={1}
                        flexWrap="wrap"
                        sx={{ mt: isMobile ? 1 : 0 }}
                      >
                        <Button
                          size="small"
                          startIcon={likedReviews.has(review.id) ? <ThumbUp /> : <ThumbUpOutlined />}
                          onClick={() => handleLikeReview(review.id)}
                          sx={{ 
                            color: likedReviews.has(review.id) ? 'primary.main' : 'text.secondary',
                            textTransform: 'none',
                            borderRadius: 2,
                            minWidth: isMobile ? 'auto' : 'unset',
                            px: isMobile ? 1 : 2
                          }}
                          disabled={!user}
                        >
                          {review.likesCount || 0}
                        </Button>
                        
                        <Button
                          size="small"
                          startIcon={<Share />}
                          onClick={async () => {
                            try {
                              const message = review.review ? `"${review.review.substring(0, 100)}..."` : `Rated ${review.rating} stars`;
                              await interactionService.shareContent('review', review.id, message);
                              setNotification({ open: true, message: 'Review shared!', severity: 'success' });
                            } catch (error) {
                              setNotification({ open: true, message: 'Review link copied!', severity: 'success' });
                            }
                          }}
                          sx={{ 
                            color: 'text.secondary',
                            textTransform: 'none',
                            borderRadius: 2,
                            minWidth: isMobile ? 'auto' : 'unset',
                            px: isMobile ? 1 : 2
                          }}
                        >
                          {isMobile ? '' : 'Share'}
                        </Button>
                        
                        <Button
                          size="small"
                          startIcon={bookmarkedReviews.has(review.id) ? <Bookmark /> : <BookmarkBorder />}
                          onClick={async () => {
                            if (!user) {
                              showAuthAlert(addNotification, navigate, 'BOOKMARKS');
                              return;
                            }
                            
                            try {
                              const isBookmarked = bookmarkedReviews.has(review.id);
                              await interactionService.toggleBookmarkUniversal('review', review.id, 'MOVIE');
                              
                              const newBookmarked = new Set(bookmarkedReviews);
                              if (isBookmarked) {
                                newBookmarked.delete(review.id);
                                setNotification({ open: true, message: 'Review removed from bookmarks', severity: 'success' });
                              } else {
                                newBookmarked.add(review.id);
                                setNotification({ open: true, message: 'Review saved to bookmarks', severity: 'success' });
                              }
                              setBookmarkedReviews(newBookmarked);
                            } catch (error) {
                              setNotification({ open: true, message: 'Failed to bookmark review', severity: 'error' });
                            }
                          }}
                          sx={{ 
                            color: bookmarkedReviews.has(review.id) ? 'warning.main' : 'text.secondary',
                            textTransform: 'none',
                            borderRadius: 2,
                            minWidth: isMobile ? 'auto' : 'unset',
                            px: isMobile ? 1 : 2
                          }}
                          disabled={!user}
                        >
                          {isMobile ? '' : 'Save'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          ))}
        </Stack>
      )}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size={isMobile ? "small" : "medium"}
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2
              }
            }}
          />
        </Box>
      )}
      
      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        {user && selectedReview?.authorID !== user.id && (
          <MenuItem onClick={() => {
            // Handle report
            setMenuAnchor(null);
          }}>
            <Flag fontSize="small" sx={{ mr: 1 }} />
            Report Review
          </MenuItem>
        )}
        {user && selectedReview?.authorID === user.id && (
          <MenuItem onClick={() => startEditReview(selectedReview)}>
            <Edit fontSize="small" sx={{ mr: 1 }} />
            Edit Review
          </MenuItem>
        )}
        {user && (selectedReview?.authorID === user.id || user.role === 'ADMIN') && (
          <MenuItem 
            onClick={() => handleDeleteReview(selectedReview.id)} 
            sx={{ color: 'error.main' }}
            disabled={deletingReview === selectedReview?.id}
          >
            <Delete fontSize="small" sx={{ mr: 1 }} />
            {deletingReview === selectedReview?.id ? 'Deleting...' : 'Delete Review'}
          </MenuItem>
        )}
      </Menu>
      
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PremiumMovieReviews;